class StoresMap {
  constructor() {
    this.map = null;
    this.markers = [];
    this.infoWindow = null;
    this.stores = [];
    this.bounds = null;

    this.init();
  }

  async init() {
    try {
      // Load stores data
      const storesDataElement = document.getElementById('stores-data');
      if (!storesDataElement) {
        console.error('Stores data not found');
        return;
      }

      this.stores = JSON.parse(storesDataElement.textContent).stores;

      // Load Google Maps API
      await this.loadGoogleMapsAPI();

      // Initialize map
      this.initMap();
    } catch (error) {
      console.error('Error initializing stores map:', error);
      this.showError('Failed to load map. Please try again later.');
    }
  }

  async loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      // Load Google Maps API - Replace 'YOUR_API_KEY' with actual API key
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAu6A3F630wm5D-T6So5LnxFoO-5OOvAmk&callback=initGoogleMaps&libraries=geometry`;
      script.async = true;
      script.defer = true;

      window.initGoogleMaps = () => {
        delete window.initGoogleMaps;
        resolve();
      };

      script.onerror = () => reject(new Error('Failed to load Google Maps API'));
      document.head.appendChild(script);
    });
  }

  initMap() {
    const mapContainer = document.getElementById('stores-map');
    if (!mapContainer) {
      console.error('Map container not found');
      return;
    }

    // Default center (New Zealand)
    const defaultCenter = { lat: -41.2865, lng: 174.7762 };

    // Initialize map
    this.map = new google.maps.Map(mapContainer, {
      center: defaultCenter,
      styles: this.getMapStyles(),
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true,
    });

    this.bounds = new google.maps.LatLngBounds();
    this.infoWindow = new google.maps.InfoWindow();

    // Add markers for all stores
    this.addStoreMarkers();

    // Fit map to show all markers
    if (this.markers.length > 0) {
      this.map.fitBounds(this.bounds);
      // Ensure minimum zoom level
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15);
        }
      });
    }

    // Hide loading indicator
    const loadingElement = mapContainer.querySelector('.stores-map__loading');
    if (loadingElement) {
      loadingElement.style.display = 'none';
    }
  }

  addStoreMarkers() {
    this.stores.forEach((store) => {
      if (!store.latitude || !store.longitude) {
        console.warn(`Store ${store.name} missing coordinates`);
        return;
      }

      const position = {
        lat: parseFloat(store.latitude),
        lng: parseFloat(store.longitude),
      };

      const marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: store.name,
        icon: this.getMarkerIcon(),
      });

      // Add to bounds
      this.bounds.extend(position);
      this.markers.push(marker);

      // Create info window content
      const infoContent = this.createInfoWindowContent(store);

      // Add click listener
      marker.addListener('click', () => {
        this.infoWindow.setContent(infoContent);
        this.infoWindow.open(this.map, marker);
      });
    });
  }

  createInfoWindowContent(store) {
    return `
      <div class="store-info-window">
        <h3 class="store-info-window__title">${this.escapeHtml(store.name)}</h3>
        ${store.address ? `<p class="store-info-window__address">${this.escapeHtml(store.address)}</p>` : ''}
        ${
          store.phone
            ? `<p class="store-info-window__phone"><a href="tel:${this.escapeHtml(store.phone)}">${this.escapeHtml(
                store.phone,
              )}</a></p>`
            : ''
        }
        <div class="store-info-window__actions">
          <a href="${store.url}" class="button button--primary button--small">View Details</a>
          ${
            store.address
              ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  store.address,
                )}" target="_blank" class="button button--secondary button--small">Get Directions</a>`
              : ''
          }
        </div>
      </div>
    `;
  }

  getMarkerIcon() {
    return {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 76 100"><path d="M38 .5C16.75.5.91 19.16.91 40.72.91 67.94 38 99.5 38 99.5s37.09-31.44 37.09-58.78C75.09 19.45 59.25.5 38 .5Z" style="fill-rule:evenodd"/><path d="M21.84 24.07c-.19.15-.38.31-.54.47-.39.38-.75.79-1.1 1.21-1.01 1.2-1.85 2.54-2.48 3.97-1.46 3.18-2.2 6.99-2.2 11.46 0 7.36 1.98 12.94 5.94 16.73 4.02 3.85 9.57 5.78 16.64 5.78h17.24v-9.25c-14.27 0-22.14-.43-23.61-1.27-2.04-1.3-3.06-3.65-3.06-7.05h26.67V36.7H28.68c0-3.45 1.05-5.8 3.14-7.05 1.64-.96 4.28-1.44 7.9-1.44h15.63v-9.32H37.01s-8.94-.36-15.18 5.2Z" style="fill:#fff"/><path d="M47.43 68.42c-.12 0-.09-.16-.09-.16l1.28-5.4s0-.03.01-.05c.01-.06.02-.11.02-.17 0-.5-.4-.9-.9-.9-.33 0-.63.18-.79.46l-6.8 11.47-.06.01-.03-.04 6.45-17.44s.03-.07.04-.1a.896.896 0 0 0-.86-1.16c-.31 0-.6.16-.76.43l-.01.02-.03.06c-.65 1.18-8.63 15.74-8.74 15.93-.08.13-.22.17-.3.17l-22.99-.05 1.24 1.83H36.57c.3 0 .58-.15.74-.39.03-.04.05-.08.07-.13l2.8-4.98h.04l.05.03-5.73 15.35c-.03.05-.05.1-.06.16a.896.896 0 0 0 .87 1.13c.32 0 .61-.17.78-.44l5.88-10 3.18-5.27h.01s.15-.18.15.02l-.04.54s-.01.07-.01.11c0 .38.28.7.65.75s18.41-.22 18.41-.22l1.11-1.8-18.03.25Z" style="fill-rule:evenodd;fill:#d41a1b"/></svg>
      `),
      scaledSize: new google.maps.Size(32, 40),
      anchor: new google.maps.Point(16, 40),
    };
  }

  getMapStyles() {
    // Custom map styling to match theme
    return [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ];
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  showError(message) {
    const mapContainer = document.getElementById('stores-map');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div class="stores-map__error">
          <p>${message}</p>
        </div>
      `;
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new StoresMap();
});
